import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X as CloseIcon, Play } from 'lucide-react';

import { Avatar, Btn } from './';
import { useAuth } from '../../context/AuthContext';
import { subscribeIncomingInvites, setInviteStatus, type GameInvite } from '../../services/gameInvitesService';
import { soundService } from '../../services/soundService';

/**
 * Surfaces incoming game invites as a top-right toast when one lands.
 * Renders globally (mounted once in App.tsx). Tapping Join navigates to
 * the inviter's room; Decline marks the invite declined.
 *
 * Stays visible until the user acts or the invite expires. Multiple
 * invites stack — most recent on top.
 */
export function IncomingInviteToast() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [invites, setInvites] = useState<GameInvite[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  /** Invite ids we have already chimed for. */
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser?.uid) {
      setInvites([]);
      return;
    }
    const unsub = subscribeIncomingInvites(currentUser.uid, rows => {
      // Chime only for invites we have NOT seen before. The subscription
      // re-fires on every write to the collection (an accept, a decline, an
      // expiry), and a toast that re-chimes every time somebody else answers
      // their own invite is a poltergeist.
      const fresh = rows.some(r => !seenRef.current.has(r.id));
      rows.forEach(r => seenRef.current.add(r.id));
      if (fresh) soundService.notify();
      setInvites(rows);
    });
    return () => { try { unsub(); } catch {} };
  }, [currentUser?.uid]);

  const visible = invites.filter(inv => !dismissed.has(inv.id));
  if (visible.length === 0) return null;

  const handleJoin = async (inv: GameInvite) => {
    await setInviteStatus(inv.id, 'accepted');
    setDismissed(prev => { const s = new Set(prev); s.add(inv.id); return s; });
    navigate(`/join/${inv.roomCode}`);
  };

  const handleDecline = async (inv: GameInvite) => {
    await setInviteStatus(inv.id, 'declined');
    setDismissed(prev => { const s = new Set(prev); s.add(inv.id); return s; });
  };

  const handleSnooze = (inv: GameInvite) => {
    // Hide for now but keep it on the Home → Recent Requests panel.
    setDismissed(prev => { const s = new Set(prev); s.add(inv.id); return s; });
  };

  return (
    <div
      role="region"
      aria-label="Incoming game invites"
      style={{
        position: 'fixed',
        top: 'max(16px, env(safe-area-inset-top, 16px))',
        right: 16,
        left: 16,
        maxWidth: 360,
        marginLeft: 'auto',
        zIndex: 250,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence>
        {visible.slice(0, 3).map(inv => (
          <motion.div
            key={inv.id}
            initial={{ opacity: 0, y: -16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="card lg"
            style={{
              padding: 12,
              background: 'var(--surface)',
              boxShadow: '0 12px 48px -12px rgba(22, 22, 26, 0.18)',
              pointerEvents: 'auto',
            }}
          >
            <div className="wr-row gap-2" style={{ alignItems: 'center', marginBottom: 8 }}>
              <Avatar id={inv.fromAvatar} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="wr-row gap-1" style={{ alignItems: 'baseline' }}>
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 14 }}>{inv.fromName}</span>
                  {inv.mode === 'async' && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--amber-soft)', color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Async</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Invited you to a {inv.mode === 'async' ? 'turn-based' : 'live'} game · {inv.roomCode}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleSnooze(inv)}
                aria-label="Snooze"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}
              >
                <CloseIcon size={16} />
              </button>
            </div>
            <div className="wr-row gap-2">
              <Btn kind="primary" full icon="Play" onClick={() => handleJoin(inv)}>Join</Btn>
              <Btn kind="ghost" full onClick={() => handleDecline(inv)}>Decline</Btn>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
